/**
 * Install PFD button — small optional UI control that triggers the
 * native browser PWA installation prompt on Chrome / Edge desktop.
 *
 *   • Hidden until the browser fires `beforeinstallprompt`.
 *   • Hidden once the app has been installed (via `appinstalled`
 *     event OR the `display-mode: standalone` media query).
 *   • Hidden entirely on browsers that don't support the prompt
 *     (Safari, Firefox mobile, unsupported versions).
 *
 * This component MUST NOT alter routing, auth, analysis logic, or any
 * data flow — it renders a single button that talks to the browser.
 */
import { useEffect, useState, useCallback } from 'react';
import { Download } from 'lucide-react';

export default function InstallPFDButton({ className = '' }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    // Detect already-installed state upfront: PWA launched via the
    // installed shortcut runs with display-mode: standalone.
    if (typeof window !== 'undefined'
        && window.matchMedia
        && window.matchMedia('(display-mode: standalone)').matches) {
      setInstalled(true);
    }

    const onBeforeInstall = (e) => {
      // Prevent Chrome ≥76's mini-infobar so we can trigger the
      // prompt from our own button on demand.
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const onClick = useCallback(async () => {
    if (!deferredPrompt) return;
    try {
      deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      // Chrome fires appinstalled separately on 'accepted' — no need
      // to duplicate state updates here.
      if (choice?.outcome !== 'accepted') {
        // User dismissed — Chrome will not re-fire beforeinstallprompt
        // until the site is revisited later. Just clear our handle.
        setDeferredPrompt(null);
      }
    } catch {
      setDeferredPrompt(null);
    }
  }, [deferredPrompt]);

  if (installed) return null;
  if (!deferredPrompt) return null;   // browser hasn't offered install

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="install-pfd-btn"
      title="Install Precision Fish Detection as a desktop app"
      className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-md border border-cyan-300 text-cyan-700 hover:bg-cyan-50 transition-colors ${className}`}
    >
      <Download className="w-3.5 h-3.5" />
      Install PFD
    </button>
  );
}
